import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import {
  selectUser,
  selectUserIsAppellant,
  selectUserIsCountyAdmin,
  selectUserMunicipalities,
  selectUserName
} from "./app";
import { RootState, AppDispatch } from ".";
import { APPEAL_STATUS_CHANGE_EVENT } from "../pages/DefaultPages/common/constants";
import dayjs from "dayjs";
import { calculateAssessmentTotal } from "../pages/DefaultPages/common/AppealCalculationUtils";
import { AdditionalContact, Appeal, AppealComparable, AppealEvent, AppealEvidence, AppealStatus, AuthorizedAttorneyContact, County, HearingType, JudgementRecord, MainContact, Municipality, PropertyClassification, PropertyImprovement, StipulationRecord, StipulationStatus, MunicipalSolicitorContact, ValuationAppeal } from "../commontypes/types";
import { PatchOperation, RestCallStatus, RestStatus, removeNull } from "../commontypes/common";

export interface PropertyValuation {
  type: string,
  land: string | number,
  improvement: string | number,
  abatement: string | number,
  total: string | number
};

interface AppealDetailState {
  appealRestStatus: RestCallStatus;
  data?: Appeal;
  county?: County;
  stipulation?: StipulationRecord;
  appealStatus?: AppealStatus;
  comparablesData: AppealComparable[];
  assessorComparablesData: AppealComparable[];
  comparablesRestStatus?: RestCallStatus;
  crossComparablesData: AppealComparable[];
  crossAssessorComparablesData: AppealComparable[];
  crossComparablesRestStatus?: RestCallStatus;
  eventsData: AppealEvent[];
  eventsRestStatus?: RestCallStatus;
  evidenceData: AppealEvidence[];
  assessorEvidenceData: AppealEvidence[];
  evidenceRestStatus?: RestCallStatus;
  crossEvidenceData: AppealEvidence[];
  crossAssessorEvidenceData: AppealEvidence[];
  crossEvidenceRestStatus?: RestCallStatus;
  improvementRecord?: PropertyImprovement;
  judgment?: JudgementRecord;
  judgmentRestStatus?: RestCallStatus;
  statusRestStatus?: RestCallStatus;
  withdrawnDate?: string;
  contact?: MainContact;
  additionalContact?: AdditionalContact;
  thirdPartyContact?: MunicipalSolicitorContact;
  authorizedAttorneyContact?: AuthorizedAttorneyContact;
  contactsRestStatus?: RestCallStatus;
  notes?: string | null;
  notesRestStatus?: RestCallStatus;
  valuation?: ValuationAppeal
  valuationRestStatus?: RestCallStatus;
  propertyValuationData: PropertyValuation[];
  petitioner?: string;
  respondent?: string;
  isAssessorAppeal: boolean;
  inLockOutPeriod: boolean;
  deadlineForEvidence: number | null;
  inEndState: boolean;
  additionPropertyClass?: PropertyClassification;
};

const initialState: AppealDetailState = {
  appealRestStatus: { status: RestStatus.NOT_STARTED },
  propertyValuationData: [
    {
      type: "Current",
      land: "",
      improvement: "",
      abatement: "",
      total: ""
    },
    {
      type: "Request",
      land: "",
      improvement: "",
      abatement: "",
      total: ""
    }
  ],
  comparablesData: [],
  assessorComparablesData: [],
  crossComparablesData: [],
  crossAssessorComparablesData: [],
  eventsData: [],
  evidenceData: [],
  assessorEvidenceData: [],
  crossEvidenceData: [],
  crossAssessorEvidenceData: [],
  isAssessorAppeal: false,
  inLockOutPeriod: false,
  deadlineForEvidence: null,
  inEndState: false,
};

const populateContactsState = (state: AppealDetailState, appeal: Appeal) => {
  state.contact = appeal.APPEAL.contact;
  state.additionalContact = appeal.APPEAL.additionalContact;
  state.thirdPartyContact = appeal.APPEAL.thirdPartyContact;
  state.authorizedAttorneyContact = appeal.APPEAL.authorizedAttorneyContact;
}

const populateJudgmentState = (state: AppealDetailState, appeal: Appeal) => {
  state.judgment = appeal.JUDGEMENT;
  state.appealStatus = appeal.APPEAL.status;
}

const populateAppealState = (state: AppealDetailState, appeal: Appeal) => {
  state.appealRestStatus = { status: RestStatus.LOADED };
  state.data = appeal;
  state.county = appeal.APPEAL.county;
  populateContactsState(state, appeal);
  populateJudgmentState(state, appeal);
  state.isAssessorAppeal = appeal.APPEAL.filingDescription.substring(0, 1) === "A";
  state.petitioner = (appeal.APPEAL.crossAppeal.isCrossAppeal && appeal.APPEAL.crossAppeal.otherAppeal?.substring(13, 14) !== "A") ||
    (!appeal.APPEAL.crossAppeal.isCrossAppeal && appeal.APPEAL.filingDescription.substring(0, 1) === "A")
    ? appeal.APPEAL.municipality.label : appeal.APPEAL.contact.firstName;
  state.respondent = (appeal.APPEAL.crossAppeal.isCrossAppeal && appeal.APPEAL.crossAppeal.otherAppeal?.substring(13, 14) === "A") ||
    (!appeal.APPEAL.crossAppeal.isCrossAppeal && appeal.APPEAL.filingDescription.substring(0, 1) !== "A")
    ? appeal.APPEAL.municipality.label : appeal.APPEAL.contact.firstName;
  state.propertyValuationData = [
    {
      type: "Current",
      land: appeal.APPEAL.property.currentAssessment.land,
      improvement: appeal.APPEAL.property.currentAssessment.buildingImprovement,
      abatement: appeal.APPEAL.property.currentAssessment.abatement,
      total: appeal.APPEAL.property.currentAssessment.total
    },
    {
      type: "Requested",
      land: "",
      improvement: "",
      abatement: "",
      total: appeal.APPEAL.valuation.requestedTotal
        ? appeal.APPEAL.valuation.requestedTotal
        : ""
    }
  ];
  state.valuation = appeal.APPEAL.valuation;


  state.additionPropertyClass = appeal.APPEAL.improvementRecord.propertyClass ? appeal.APPEAL.improvementRecord.propertyClass
    : appeal.APPEAL.improvementRecord.originalRecord?.propertyClass ? appeal.APPEAL.improvementRecord.originalRecord?.propertyClass
      : appeal.APPEAL.property.classification;

  const hearingDateExists = appeal.APPEAL.hearingDate ? true : false;
  let isWithinSevenDaysOfHearing = false;
  let deadlineForEvidence = null;
  if (hearingDateExists) {
    if (appeal.APPEAL.county.key === '03') {
      deadlineForEvidence = dayjs(appeal.APPEAL.hearingDate).endOf('day').subtract(7, 'days').valueOf();
    } else {
      deadlineForEvidence = dayjs(appeal.APPEAL.hearingDate).hour(16).minute(0).second(0).millisecond(0).subtract(7, 'days').valueOf();
    }
    isWithinSevenDaysOfHearing = dayjs(deadlineForEvidence).isBefore(dayjs());
  }
  const isInEndState = ((appeal.APPEAL.status && (appeal.APPEAL.status.toUpperCase() === AppealStatus.JudgmentIssued.toUpperCase())) ||
    appeal.APPEAL.status === AppealStatus.Stipulated || appeal.APPEAL.status === AppealStatus.Withdrawn);

  state.inLockOutPeriod = isWithinSevenDaysOfHearing;
  state.deadlineForEvidence = deadlineForEvidence;
  state.inEndState = isInEndState;
  state.improvementRecord = appeal.APPEAL.improvementRecord;
  state.stipulation = appeal.STIPULATION
};

export type FetchAppealParams = {
  appealNumber: string;
  county: County;
};


export const fetchAppeal = createAsyncThunk<
  { appeal: Appeal, userIsAppellant: boolean, username: string, userMunicipalities?: Municipality[] },
  FetchAppealParams,
  { state: RootState; getState: () => RootState, dispatch: AppDispatch; requestId: string }
>(
  'appealDetail/fetchAppeal',
  async ({ appealNumber, county }, { getState, dispatch, requestId }) => {
    const response = await axios
      .get(
        `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_STAGE}/v2/${county.key}/appeals/${encodeURIComponent(appealNumber.trim())}`,
      );
    const state = getState();
    const userIsAppellant = selectUserIsAppellant(state);
    let username = selectUserName(state);
    if (!username) {
      throw new Error("User name not found");
    }
    const userMunicipalities = selectUserMunicipalities(state);
    dispatch(fetchComparables({ appealNumber }));
    dispatch(fetchEvidence({ appealNumber }));
    dispatch(fetchEvents({ appealNumber, source: requestId }));
    if (response.data?.appeal && response.data.appeal.APPEAL.crossAppeal.otherAppeal) {
      dispatch(fetchComparables({ appealNumber: response.data.appeal.APPEAL.crossAppeal.otherAppeal, isFromCrossAppeal: true }));
      dispatch(fetchEvidence({ appealNumber: response.data.appeal.APPEAL.crossAppeal.otherAppeal, isFromCrossAppeal: true }));
    }
    return { appeal: response.data.appeal, userIsAppellant, username, userMunicipalities };
  }
);

export type FetchComparablesParams = {
  appealNumber: string;
  isFromCrossAppeal?: boolean;
}
export const fetchComparables = createAsyncThunk<
  { comparables: AppealComparable[], isFromCrossAppeal?: boolean },
  FetchComparablesParams,
  { state: RootState; getState: () => RootState }
>(
  'appealDetail/fetchComparables',
  async ({ appealNumber, isFromCrossAppeal }, { getState }) => {
    console.log("get Comparables");
    const state = getState();
    const county = selectCounty(state);
    if (!county) {
      throw new Error("County not found");
    }
    const response = await axios
      .get(
        `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_STAGE}/v2/${county.key}/appeals/${encodeURIComponent(appealNumber.trim())}/comparables`,
        {
          params: {
          }
        }
      );
    return { comparables: response.data.comparables, isFromCrossAppeal: isFromCrossAppeal };
  }
);

export type AddComparableParams = {
  blq: string,
  address: string,
  salePrice: string | number,
  saleDate: string,
  isAssessorComp: boolean
}


export const addComparable = createAsyncThunk<
  AppealComparable,
  AddComparableParams,
  { state: RootState; getState: () => RootState; dispatch: AppDispatch; requestId: string }
>(
  'appealDetail/addComparable',
  async ({ blq, address, salePrice, saleDate, isAssessorComp }, { getState, dispatch, requestId }) => {
    console.log("add Comparable");
    const state = getState();
    const county = selectCounty(state);
    const appealNumber = selectAppealNumber(state);
    if (!county) {
      throw new Error("County not found");
    }
    if (!appealNumber) {
      throw new Error("Appeal number not found");
    }
    if (!blq || !address || !salePrice || !saleDate) {
      throw new Error("Missing required fields for comparable");
    }
    const response = await axios
      .post(
        `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_STAGE}/v2/${county.key}/appeals/${encodeURIComponent(appealNumber.trim())}/comparables`,
        {
          comparable: {
            qual: blq,
            address: address,
            salePrice: salePrice,
            saleDate: saleDate,
            assessor: isAssessorComp
          }
        },
        {
        }
      );

    dispatch(addEvent({ eventText: "Comparable Added", source: requestId }));
    if (!isAssessorComp && selectAppealStatus(state) !== AppealStatus.WorkInProgress) {
      dispatch(updateAppealStatus({ appealStatus: AppealStatus.WorkInProgress }));
    }
    dispatch(fetchComparables({ appealNumber }));
    return response.data.comparable;
  }
);

export const deleteComparable = createAsyncThunk<
  AppealComparable,
  AppealComparable,
  { state: RootState; getState: () => RootState; dispatch: AppDispatch; requestId: string }
>(
  'appealDetail/deleteComparable',
  async (comp, { getState, dispatch, requestId }) => {
    console.log("delete Comparable");
    const state = getState();
    const county = selectCounty(state);
    const appealNumber = selectAppealNumber(state);
    if (!county) {
      throw new Error("County not found");
    }
    if (!appealNumber) {
      throw new Error("Appeal number not found");
    }

    const response = await axios
      .delete(
        `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_STAGE}/v2/${county.key}/appeals/${encodeURIComponent(appealNumber.trim())}/comparables/${comp._CID.trim()}`,
        {
          params: {
          }
        }
      );
    dispatch(addEvent({ eventText: "Comparable Deleted", source: requestId }));
    dispatch(fetchComparables({ appealNumber: comp._APPEAL_NUMBER.substring(2) }));
    return response.data.comparable;
  }
);
export type AddEventParams = {
  eventText: string;
  source?: string;
}

export const addEvent = createAsyncThunk<
  string,
  AddEventParams,
  { state: RootState; getState: () => RootState; dispatch: AppDispatch; requestId: string }
>(
  'appealDetail/addEvent',
  async ({ eventText, source }, { getState, dispatch, requestId }) => {
    const state = getState();
    const appealNumber = selectAppealNumber(state);
    const county = selectCounty(state);
    if (!county) {
      throw new Error("County not found");
    }
    if (!appealNumber) {
      throw new Error("Appeal number not found");
    }
    const response = await axios
      .post(
        `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_STAGE}/v2/${county.key}/appeals/${encodeURIComponent(appealNumber.trim())}/events`,
        {
          eventText: eventText
        },
        {
        }
      );
    dispatch(fetchEvents({ appealNumber, source: source || requestId }));
    return response.data.eventId;
  }
);

export type FetchEventParams = {
  appealNumber: string;
  source?: string;
}


export const fetchEvents = createAsyncThunk<
  { events: AppealEvent[], userIsAppellant: boolean, username: string, source?: string },
  FetchEventParams,
  { state: RootState; getState: () => RootState }
>(
  'appealDetail/fetchEvents',
  async ({ appealNumber, source }, { getState }) => {
    console.log("get Events");
    const state = getState();
    const county = selectCounty(state);
    const userIsAppellant = selectUserIsAppellant(state);
    const username = selectUserName(state);
    if (!username) {
      throw new Error("User name not found");
    }
    if (!county) {
      throw new Error("County not found");
    }
    if (!appealNumber) {
      throw new Error("Appeal number not found");
    }
    const response = await axios
      .get(
        `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_STAGE}/v2/${county.key}/appeals/${encodeURIComponent(appealNumber.trim())}/events`,
        {
        }
      );
    return { events: response.data.events as AppealEvent[], userIsAppellant, username, source };
  }
);

export type AddEvidenceDigestParams = {
  isAssessor: boolean,
  evidenceIds: string[],
  fileName: string
}


export const addEvidenceDigest = createAsyncThunk<
  AppealComparable,
  AddEvidenceDigestParams,
  { state: RootState; getState: () => RootState; dispatch: AppDispatch; requestId: string }
>(
  'appealDetail/addEvidenceDigest',
  async ({ fileName, isAssessor, evidenceIds }, { getState, dispatch, requestId }) => {
    console.log("add Evidence Digest");
    const state = getState();
    const county = selectCounty(state);
    const appealNumber = selectAppealNumber(state);
    if (!county) {
      throw new Error("County not found");
    }
    if (!appealNumber) {
      throw new Error("Appeal number not found");
    }
    if (isAssessor === undefined || !evidenceIds || !fileName) {
      throw new Error("Missing required fields for evidence digest");
    }
    const response = await axios
      .post(
        `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_STAGE}/v2/${county.key}/appeals/${encodeURIComponent(appealNumber.trim())}/evdigest`,
        {
          fileName,
          isAssessorEvidence: isAssessor,
          evidenceIDs: evidenceIds
        },
        {}
      );

    dispatch(addEvent({ eventText: "Evidence Digest Added", source: requestId }));
    dispatch(fetchEvidence({ appealNumber }));
    return response.data.evidenceDigest;
  }
);
export type FetchEvidenceParams = {
  appealNumber: string;
  isFromCrossAppeal?: boolean;
}

export const fetchEvidence = createAsyncThunk<
  { evidence: AppealEvidence[], isFromCrossAppeal?: boolean },
  FetchEvidenceParams,
  { state: RootState; getState: () => RootState }
>(
  'appealDetail/fetchEvidence',
  async ({ appealNumber, isFromCrossAppeal }, { getState }) => {
    console.log("get Evidence");
    const state = getState();
    const county = selectCounty(state);
    if (!county) {
      throw new Error("County not found");
    }

    const response = await axios
      .get(
        `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_STAGE}/v2/${county.key}/appeals/${appealNumber}/evidence`,
        {
          params: {
          }
        }
      );
    return { evidence: response.data.evidence, isFromCrossAppeal };
  }
);

export type UpdateAppealStatusParams = {
  appealStatus: AppealStatus;
  notify?: boolean;
}
export const updateAppealStatus = createAsyncThunk<
  Appeal,
  UpdateAppealStatusParams,
  { state: RootState; getState: () => RootState; dispatch: AppDispatch; requestId: string }
>(
  'appealDetail/updateAppealStatus',
  async ({ appealStatus }, { getState, dispatch, requestId }) => {
    const state = getState();
    const county = selectCounty(state);
    if (!county) {
      throw new Error("County not found");
    }
    const appealNumber = selectAppealNumber(state);
    if (!appealNumber) {
      throw new Error("Appeal number not found");
    }
    const currentStatus = selectAppealStatus(state);
    const patchOperations: PatchOperation[] = [
      {
        "op": "replace",
        "path": "APPEAL/status",
        "value": appealStatus
      }
    ]
    if (currentStatus === AppealStatus.Withdrawn && appealStatus !== AppealStatus.Withdrawn &&
      appealStatus !== AppealStatus.JudgmentIssued) {
      patchOperations.push({
        "op": "remove",
        "path": "APPEAL/reasonForWithdrawal"
      });
    }
    const response = await axios
      .patch(
        `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_STAGE}/v2/${county.key}/appeals/${encodeURIComponent(appealNumber)}`,
        {
          sendNotifications: true,
          patchOperations
        }
      );
    dispatch(fetchEvents({ appealNumber, source: requestId }));
    return response.data.appeal;
  }
);

export type UpdateContactsParams = {
  contact: MainContact;
  additionalContact: AdditionalContact;
  thirdPartyContact: MunicipalSolicitorContact;
  authorizedAttorneyContact: AuthorizedAttorneyContact;
}

export const updateContacts = createAsyncThunk<
  Appeal,
  UpdateContactsParams,
  { state: RootState; getState: () => RootState; dispatch: AppDispatch; requestId: string }
>(
  'appealDetail/updateContacts',
  async ({ contact, additionalContact, thirdPartyContact, authorizedAttorneyContact }, { getState }) => {
    const state = getState();
    const county = selectCounty(state);
    if (!county) {
      throw new Error("County not found");
    }
    const appealNumber = selectAppealNumber(state);
    if (!appealNumber) {
      throw new Error("Appeal number not found");
    }
    const response = await axios
      .patch(
        `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_STAGE}/v2/${county.key}/appeals/${encodeURIComponent(appealNumber)}`,
        {
          sendNotifications: true,
          patchOperations: [
            {
              "op": "replace",
              "path": "APPEAL/contact",
              "value": contact
            },
            {
              "op": "replace",
              "path": "APPEAL/additionalContact",
              "value": additionalContact
            },
            {
              "op": "replace",
              "path": "APPEAL/thirdPartyContact",
              "value": thirdPartyContact
            },
            {
              "op": "replace",
              "path": "APPEAL/authorizedAttorneyContact",
              "value": authorizedAttorneyContact
            }
          ]
        }
      );
    return response.data.appeal;
  }

);

export type UpdateValuationParams = {
  reasonForAppeal: string;
  hearingType: HearingType;
  taxCourtPending: boolean;
  awop: boolean;
  paymentMethod: string;
  receiptId?: string;
  requestedTotal?: number | null;
  purchasePrice?: number | null;
  purchaseDate?: string | null;
}
export const updateValuation = createAsyncThunk<
  Appeal,
  UpdateValuationParams,
  { state: RootState; getState: () => RootState; dispatch: AppDispatch; requestId: string }
>(
  'appealDetail/updateValuation',
  async (
    { reasonForAppeal, hearingType, taxCourtPending, awop, paymentMethod, receiptId, requestedTotal, purchasePrice, purchaseDate },
    { getState }
  ) => {
    if (!hearingType) {
      throw new Error("Hearing type not found");
    }
    const state = getState();
    const county = selectCounty(state);
    if (!county) {
      throw new Error("County not found");
    }
    const appealNumber = selectAppealNumber(state);
    if (!appealNumber) {
      throw new Error("Appeal number not found");
    }
    const valuationObj = {
      appealing: selectIsAppealingValuation(state),
      purchaseDate,
      purchasePrice,
      reason: reasonForAppeal,
      taxCourtPending,
      awop,
      requestedTotal
    };
    removeNull(valuationObj);

    console.log(valuationObj);

    const response = await axios
      .patch(
        `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_STAGE}/v2/${county.key}/appeals/${encodeURIComponent(appealNumber)}`,
        {
          sendNotifications: true,
          patchOperations: [
            {
              "op": "replace",
              "path": "APPEAL/valuation",
              "value": valuationObj
            },
            {
              "op": "replace",
              "path": "APPEAL/receiptId",
              "value": receiptId
            },
            {
              "op": "replace",
              "path": "APPEAL/entryPaymentMethod",
              "value": paymentMethod
            },
            {
              "op": "replace",
              "path": "APPEAL/hearingType",
              "value": hearingType
            }
          ]
        }
      );
    return response.data.appeal;
  }
);

export type UpdateImprovementAdditionParams = {
  additionDate: string;
  additionLand: number | undefined;
  additionImprovement: number | undefined;
  additionAbatement: number | undefined;
  additionMonth: number | undefined;
}

export const updateImprovementAddition = createAsyncThunk<
  Appeal,
  UpdateImprovementAdditionParams,
  { state: RootState; getState: () => RootState; dispatch: AppDispatch; requestId: string }
>(
  'appealDetail/updateImprovementAddition',
  async (
    { additionDate, additionLand, additionImprovement, additionAbatement, additionMonth },
    { getState, dispatch, requestId }
  ) => {
    const state = getState();
    const county = selectCounty(state);
    if (!county) {
      throw new Error("County not found");
    }
    const appealNumber = selectAppealNumber(state);
    if (!appealNumber) {
      throw new Error("Appeal number not found");
    }
    const improvementRecord = selectImprovementRecord(state);
    if (!improvementRecord) {
      throw new Error("Improvement record not found");
    }
    const additionPropertyClass = selectAdditionPropertyClass(state);

    const updatedImprovement = {
      ...improvementRecord,
      requestedAdditionDate: additionDate,
      requestedAdditionLand: additionLand,
      requestedAdditionImprovement: additionImprovement,
      requestedAdditionAbatement: additionAbatement,
      requestedAdditionMonth: additionMonth,
      requestedAdditionAmount: calculateAssessmentTotal(additionPropertyClass, additionLand, additionImprovement, additionAbatement)
    };

    removeNull(updatedImprovement);


    console.log(updatedImprovement);

    const response = await axios
      .patch(
        `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_STAGE}/v2/${county.key}/appeals/${encodeURIComponent(appealNumber)}`,
        {
          sendNotifications: false,
          patchOperations: [
            {
              "op": "replace",
              "path": "APPEAL/improvementRecord",
              "value": updatedImprovement
            },
          ]
        }
      );
    dispatch(fetchEvents({ appealNumber, source: requestId }));
    return response.data.appeal;
  }
);

export const updateNotes = createAsyncThunk<
  Appeal,
  string,
  { state: RootState; getState: () => RootState; dispatch: AppDispatch; requestId: string }
>(
  'appealDetail/updateNotes',
  async (notes, { getState }) => {
    //console.log("Saving notes:" + notes);
    const state = getState();
    const county = selectCounty(state);
    if (!county) {
      throw new Error("County not found");
    }
    const appealNumber = selectAppealNumber(state);
    if (!appealNumber) {
      throw new Error("Appeal number not found");
    }
    const response = await axios
      .patch(
        `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_STAGE}/v2/${county.key}/appeals/${encodeURIComponent(appealNumber)}`,
        {
          sendNotifications: true,
          patchOperations: [
            {
              "op": "replace",
              "path": "APPEAL/notes",
              "value": notes
            }
          ]
        }
      );
    return response.data.appeal;
  }
);

export type RenumberAppealParams = {
  newAppealNumber: string;
  newOtherAppealNumber?: string,
  municipalContact?: MunicipalSolicitorContact;
  attorneyContact?: AdditionalContact;
}

export const renumberAppeal = createAsyncThunk<
  Appeal,
  RenumberAppealParams,
  { state: RootState; getState: () => RootState; dispatch: AppDispatch; requestId: string }
>(
  'appealDetail/renumberAppeal',
  async ({ newAppealNumber, newOtherAppealNumber, municipalContact, attorneyContact }, { getState }) => {
    const state = getState();
    const county = selectCounty(state);
    if (!county) {
      throw new Error("County not found");
    }
    const appealNumber = selectAppealNumber(state);
    if (!appealNumber) {
      throw new Error("Appeal number not found");
    }
    if (!newAppealNumber) {
      throw new Error("New appeal number not found");
    }
    const response = await axios
      .put(
        `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_STAGE}/v2/${county.key}/appeals/${encodeURIComponent(appealNumber)}`,
        {
          newAppealNumber,
          newOtherAppealNumber,
          municipalContact,
          attorneyContact
        }
      );
    return response.data.appeal;
  }
);

export type AddJudgmentToAppealArgs = {
  judgment: JudgementRecord;
  sendNotifications: boolean;
}


export const addJudgmentToAppeal = createAsyncThunk<
  Appeal,
  AddJudgmentToAppealArgs,
  { state: RootState; getState: () => RootState; dispatch: AppDispatch; requestId: string }
>(
  'appealDetail/addJudgmentToAppeal',
  async ({ judgment, sendNotifications }, { getState, dispatch, requestId }) => {
    const state = getState();
    const newJudgment = !selectJudgmentAddedDate(state);
    const county = selectCounty(state);
    if (!county) {
      throw new Error("County not found");
    }
    const appealNumber = selectAppealNumber(state);
    const appealStatus = selectAppealStatus(state);
    if (!appealNumber) {
      throw new Error("Appeal number not found");
    }
    const patchOperations: PatchOperation[] = [
      {
        "op": newJudgment ? "add" : "replace",
        "path": "JUDGEMENT",
        "value": judgment
      }
    ];
    if (judgment.judgementResult && judgment.judgementMailDate && appealStatus !== AppealStatus.JudgmentIssued) {
      patchOperations.push({
        "op": "replace",
        "path": "APPEAL/status",
        "value": AppealStatus.JudgmentIssued
      });
    }

    const response = await axios
      .patch(
        `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_STAGE}/v2/${county.key}/appeals/${encodeURIComponent(appealNumber)}`,
        {
          sendNotifications,
          patchOperations
        }
      );
    dispatch(fetchEvents({ appealNumber, source: requestId }));
    return response.data.appeal;
  }
);

export const deleteJudgment = createAsyncThunk<
  Appeal,
  { newAppealStatus: AppealStatus },
  { state: RootState; getState: () => RootState; dispatch: AppDispatch; requestId: string }
>(
  'appealDetail/deleteJudgment',
  async ({ newAppealStatus }, { getState, dispatch, requestId }) => {
    console.log("delete Judgment");
    const state = getState();
    const county = selectCounty(state);
    if (!county) {
      throw new Error("County not found");
    }
    const appealNumber = selectAppealNumber(state);
    if (!appealNumber) {
      throw new Error("Appeal number not found");
    }

    const response = await axios
      .patch(
        `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_STAGE}/v2/${county.key}/appeals/${encodeURIComponent(appealNumber)}`,
        {
          sendNotifications: false,
          patchOperations: [
            {
              op: "remove",
              path: "JUDGEMENT"
            },
            {
              op: "replace",
              path: "APPEAL/status",
              value: newAppealStatus
            }
          ]
        }
      );
    dispatch(fetchEvents({ appealNumber, source: requestId }));
    return response.data.appeal;
  }
);

export const approveAppeal = createAsyncThunk<
  Appeal,
  {},
  { state: RootState; getState: () => RootState; dispatch: AppDispatch; requestId: string }
>(
  'appealDetail/approveAppeal',
  async ({ }, { getState, dispatch, requestId }) => {
    const state = getState();
    const county = selectCounty(state);
    if (!county) {
      throw new Error("County not found");
    }
    const appealNumber = selectAppealNumber(state);
    if (!appealNumber) {
      throw new Error("Appeal number not found");
    }
    const user = selectUser(state);
    const { County, Municipalities, ...approverObj } = { ...user, signedDate: Date.now() };

    const response = await axios
      .patch(
        `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_STAGE}/v2/${county.key}/appeals/${encodeURIComponent(appealNumber)}`,
        {
          sendNotifications: false,
          patchOperations: [
            {
              op: "replace",
              path: "APPEAL/approver",
              value: approverObj
            },
            {
              op: "replace",
              path: "APPEAL/approved",
              value: true
            }
          ]
        }
      );
    dispatch(fetchEvents({ appealNumber, source: requestId }));
    return response.data.appeal;
  }
);

export const updateScanFile = createAsyncThunk<
  Appeal,
  { scanFile: string },
  { state: RootState; getState: () => RootState; dispatch: AppDispatch; requestId: string }
>(
  'appealDetail/updateScanFile',
  async ({ scanFile }, { getState, dispatch, requestId }) => {
    const state = getState();
    const county = selectCounty(state);
    if (!county) {
      throw new Error("County not found");
    }
    const appealNumber = selectAppealNumber(state);
    if (!appealNumber) {
      throw new Error("Appeal number not found");
    }

    const response = await axios
      .patch(
        `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_STAGE}/v2/${county.key}/appeals/${encodeURIComponent(appealNumber)}`,
        {
          sendNotifications: false,
          patchOperations: [
            {
              op: "replace",
              path: "APPEAL/scanFile",
              value: scanFile
            }
          ]
        }
      );
    dispatch(fetchEvents({ appealNumber, source: requestId }));
    return response.data.appeal;
  }
);

export const addStipulation = createAsyncThunk<
  Appeal,
  { stipulation: StipulationRecord },
  { state: RootState; getState: () => RootState; dispatch: AppDispatch; requestId: string }
>(
  'appealDetail/addStipulation',
  async ({ stipulation }, { getState, dispatch, requestId }) => {
    const state = getState();
    const county = selectCounty(state);
    if (!county) {
      throw new Error("County not found");
    }
    const appealNumber = selectAppealNumber(state);
    if (!appealNumber) {
      throw new Error("Appeal number not found");
    }
    const patchOperations: PatchOperation[] = [
      {
        "op": "add",
        "path": "STIPULATION",
        "value": stipulation
      }
    ];
    if (stipulation.fileName) {
      patchOperations.push({
        op: "replace",
        path: "APPEAL/status",
        value: AppealStatus.Stipulated
      });
    }
    const response = await axios
      .patch(
        `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_STAGE}/v2/${county.key}/appeals/${encodeURIComponent(appealNumber)}`,
        {
          sendNotifications: true,
          patchOperations
        }
      );
    dispatch(fetchEvents({ appealNumber, source: requestId }));
    return response.data.appeal;
  }
);

export const deleteStipulation = createAsyncThunk<
  Appeal,
  {},
  { state: RootState; getState: () => RootState; dispatch: AppDispatch; requestId: string }
>(
  'appealDetail/deleteStipulation',
  async ({ }, { getState, dispatch, requestId }) => {
    const state = getState();
    const county = selectCounty(state);
    if (!county) {
      throw new Error("County not found");
    }
    const appealNumber = selectAppealNumber(state);
    if (!appealNumber) {
      throw new Error("Appeal number not found");
    }
    const userIsCountyAdmin = selectUserIsCountyAdmin(state);
    const appealStatus = selectAppealStatus(state);
    const patchOperations: PatchOperation[] = [
      {
        op: "remove",
        path: "STIPULATION",
      }
    ];
    if (userIsCountyAdmin && appealStatus !== AppealStatus.WorkInProgress) {
      patchOperations.push({
        op: "replace",
        path: "APPEAL/status",
        value: AppealStatus.WorkInProgress
      }
      )
    }
    const response = await axios
      .patch(
        `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_STAGE}/v2/${county.key}/appeals/${encodeURIComponent(appealNumber)}`,
        {
          sendNotifications: true,
          patchOperations
        }
      );
    dispatch(fetchEvents({ appealNumber, source: requestId }));
    return response.data.appeal;
  }
);

export type UpdateStipulationStatusParams = {
  status: StipulationStatus;
  appellantObj?: MainContact;
  solicitorObj?: MainContact;
}

export const updateStipulationStatus = createAsyncThunk<
  Appeal,
  UpdateStipulationStatusParams,
  { state: RootState; getState: () => RootState; dispatch: AppDispatch; requestId: string }
>(
  'appealDetail/updateStipulationStatus',
  async ({ status, appellantObj, solicitorObj }, { getState, dispatch, requestId }) => {
    const state = getState();
    const county = selectCounty(state);
    if (!county) {
      throw new Error("County not found");
    }
    const appealNumber = selectAppealNumber(state);
    if (!appealNumber) {
      throw new Error("Appeal number not found");
    }
    const patchOperations: PatchOperation[] = [
      {
        "op": "replace",
        "path": "STIPULATION/status",
        "value": status
      }
    ];
    if (appellantObj) {
      patchOperations.push({
        op: "replace",
        path: "STIPULATION/appellantObj",
        value: appellantObj
      });
    }
    if (solicitorObj) {
      patchOperations.push({
        op: "replace",
        path: "STIPULATION/solicitorObj",
        value: solicitorObj
      });
    }
    if (status === StipulationStatus.SOLICITOR_APPROVED) {
      patchOperations.push({
        op: "replace",
        path: "APPEAL/status",
        value: AppealStatus.Stipulated
      });
    }
    const response = await axios
      .patch(
        `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_STAGE}/v2/${county.key}/appeals/${encodeURIComponent(appealNumber)}`,
        {
          sendNotifications: true,
          patchOperations
        }
      );
    dispatch(fetchEvents({ appealNumber, source: requestId }));
    return response.data.appeal;
  }
);

export const withdrawAppeal = createAsyncThunk<
  Appeal,
  { reasonForWithdrawal: string },
  { state: RootState; getState: () => RootState; dispatch: AppDispatch; requestId: string }
>(
  'appealDetail/withdrawAppeal',
  async ({ reasonForWithdrawal }, { getState, dispatch, requestId }) => {
    const state = getState();
    const county = selectCounty(state);
    if (!county) {
      throw new Error("County not found");
    }
    const appealNumber = selectAppealNumber(state);
    if (!appealNumber) {
      throw new Error("Appeal number not found");
    }

    const response = await axios
      .patch(
        `${process.env.REACT_APP_API_URL}/${process.env.REACT_APP_STAGE}/v2/${county.key}/appeals/${encodeURIComponent(appealNumber)}`,
        {
          sendNotifications: true,
          patchOperations: [
            {
              "op": "add",
              "path": "APPEAL/reasonForWithdrawal",
              "value": reasonForWithdrawal
            },
            {
              "op": "replace",
              "path": "APPEAL/status",
              "value": AppealStatus.Withdrawn
            }
          ]
        }
      );
    dispatch(fetchEvents({ appealNumber, source: requestId }));
    return response.data.appeal;
  }
);

export const appealDetailSlice = createSlice({
  name: 'appealDetail',
  initialState: initialState,
  reducers: {
    setCounty: (state, action) => {
      state.county = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(addEvent.pending, (state, action) => {
        state.eventsRestStatus = { status: RestStatus.SAVING, source: action.meta.arg.source, requestId: action.meta.requestId };
      })
      .addCase(addEvent.fulfilled, (state) => {
        state.eventsRestStatus!.status = RestStatus.SAVED;
      })
      .addCase(addEvent.rejected, (state, action) => {
        state.eventsRestStatus!.status = RestStatus.SAVE_FAILED;
        state.eventsRestStatus!.error = action.error.message;
      })
      .addCase(addStipulation.pending, (state) => {
        state.statusRestStatus = { status: RestStatus.SAVING };
      })
      .addCase(addStipulation.fulfilled, (state, action) => {
        state.stipulation = action.payload.STIPULATION;
        state.appealStatus = action.payload.APPEAL.status;
        state.statusRestStatus = { status: RestStatus.SAVED };
      })
      .addCase(addStipulation.rejected, (state, action) => {
        state.statusRestStatus = { status: RestStatus.SAVE_FAILED, error: action.error.message };
      })
      .addCase(deleteStipulation.pending, (state) => {
        state.statusRestStatus = { status: RestStatus.SAVING };
      })
      .addCase(deleteStipulation.fulfilled, (state, action) => {
        state.stipulation = undefined
        state.appealStatus = action.payload.APPEAL.status;
        state.statusRestStatus = { status: RestStatus.SAVED };
      })
      .addCase(deleteStipulation.rejected, (state, action) => {
        state.statusRestStatus = { status: RestStatus.SAVE_FAILED, error: action.error.message };
      })
      .addCase(updateStipulationStatus.pending, (state) => {
        state.statusRestStatus = { status: RestStatus.SAVING };
      })
      .addCase(updateStipulationStatus.fulfilled, (state, action) => {
        state.stipulation = action.payload.STIPULATION;
        state.appealStatus = action.payload.APPEAL.status;
        state.statusRestStatus = { status: RestStatus.SAVED };
      })
      .addCase(updateStipulationStatus.rejected, (state, action) => {
        state.statusRestStatus = { status: RestStatus.SAVE_FAILED, error: action.error.message };
      })
      .addCase(fetchAppeal.pending, (state, action) => {
        state.appealRestStatus = { status: RestStatus.LOADING, requestId: action.meta.requestId };
      })
      .addCase(fetchAppeal.fulfilled, (state, action) => {
        const { appeal, userIsAppellant, username, userMunicipalities } = action.payload;
        if (!appeal) {
          state.appealRestStatus.status = RestStatus.LOAD_FAILED;
          state.appealRestStatus.error = 'Appeal was not found';
        } else {
          if (userIsAppellant) {
            if (appeal._UUID !== username) {
              state.appealRestStatus = { status: RestStatus.LOAD_FAILED, error: 'Appeal was not found' }
              return;
            }
          } else {
            if (userMunicipalities && !userMunicipalities.find(m => appeal.APPEAL.municipality.key === m.key)) {
              state.appealRestStatus = { status: RestStatus.LOAD_FAILED, error: 'Appeal was not found' };
              return;
            }
          }
          populateAppealState(state, appeal);
        }
      })
      .addCase(fetchAppeal.rejected, (state, action) => {
        state.appealRestStatus = { status: RestStatus.LOAD_FAILED, error: action.error.message };
      })
      .addCase(fetchComparables.pending, (state, action) => {
        if (!action.meta.arg.isFromCrossAppeal) {
          state.comparablesRestStatus = { status: RestStatus.LOADING };
          state.comparablesData = [];
          state.assessorComparablesData = [];
        } else {
          state.crossComparablesRestStatus = { status: RestStatus.LOADING };
          state.crossComparablesData = [];
          state.crossAssessorComparablesData = [];
        }
      })
      .addCase(fetchComparables.fulfilled, (state, action) => {
        const comparables = action.payload.comparables;
        if (!action.meta.arg.isFromCrossAppeal) {
          state.comparablesData = comparables.filter(
            obj => obj.assessor !== true
          );
          state.assessorComparablesData = comparables.filter(
            obj => obj.assessor === true
          );
          state.comparablesRestStatus = { status: RestStatus.LOADED };
        } else {
          state.crossComparablesData = comparables.filter(
            obj => obj.assessor !== true
          );
          state.crossAssessorComparablesData = comparables.filter(
            obj => obj.assessor === true
          );
          state.crossComparablesRestStatus = { status: RestStatus.LOADED };
        }
      })
      .addCase(fetchComparables.rejected, (state, action) => {
        if (!action.meta.arg.isFromCrossAppeal) {
          state.comparablesRestStatus = { status: RestStatus.LOAD_FAILED, error: action.error.message };
        } else {
          state.crossComparablesRestStatus = { status: RestStatus.LOAD_FAILED, error: action.error.message };
        }
      })
      .addCase(addComparable.pending, (state) => {
        state.comparablesRestStatus = { status: RestStatus.SAVING };
      })
      .addCase(addComparable.fulfilled, (state) => {
        state.comparablesRestStatus = { status: RestStatus.SAVED };
      })
      .addCase(addComparable.rejected, (state, action) => {
        state.comparablesRestStatus = { status: RestStatus.SAVE_FAILED, error: action.error.message };
      })
      .addCase(deleteComparable.pending, (state) => {
        state.comparablesRestStatus = { status: RestStatus.SAVING };
      })
      .addCase(deleteComparable.fulfilled, (state) => {
        state.comparablesRestStatus = { status: RestStatus.SAVED };
      })
      .addCase(deleteComparable.rejected, (state, action) => {
        state.comparablesRestStatus = { status: RestStatus.SAVE_FAILED, error: action.error.message };
      })
      .addCase(addJudgmentToAppeal.pending, (state, action) => {
        const { requestId } = action.meta;
        state.judgmentRestStatus = { status: RestStatus.SAVING, requestId };
      })
      .addCase(addJudgmentToAppeal.fulfilled, (state, action) => {
        populateAppealState(state, action.payload);
        state.judgmentRestStatus = { status: RestStatus.SAVED };
      })
      .addCase(addJudgmentToAppeal.rejected, (state, action) => {
        state.judgmentRestStatus!.status = RestStatus.SAVE_FAILED;
        state.judgmentRestStatus!.error = action.error.message;
      })
      .addCase(deleteJudgment.pending, (state, action) => {
        const { requestId } = action.meta;
        state.judgmentRestStatus = { status: RestStatus.SAVING, requestId };
      })
      .addCase(deleteJudgment.fulfilled, (state, action) => {
        populateAppealState(state, action.payload);
        state.judgmentRestStatus = { status: RestStatus.SAVED };
      })
      .addCase(deleteJudgment.rejected, (state, action) => {
        state.judgmentRestStatus!.status = RestStatus.SAVE_FAILED;
        state.judgmentRestStatus!.error = action.error.message;
      })
      .addCase(fetchEvents.pending, (state, action) => {
        state.eventsData = [];
        state.eventsRestStatus = { status: RestStatus.LOADING, requestId: action.meta.requestId, source: action.meta.arg?.source };
      })
      .addCase(fetchEvents.fulfilled, (state, action) => {
        const { events, userIsAppellant, username } = action.payload;
        //console.log("***** getEvents response", events);
        if (userIsAppellant) {
          if (events !== null) {
            state.eventsData = events.filter(obj => obj._UUID === username);
          }
        } else {
          if (events !== null) {
            state.eventsData = events;
          }
        }
        //Get withdrawn date from event
        const withdrawnEvents = events.filter(i => i.eventText === APPEAL_STATUS_CHANGE_EVENT + AppealStatus.Withdrawn);
        if (withdrawnEvents.length > 0) {
          state.withdrawnDate = withdrawnEvents[0].time;
        }
        state.eventsRestStatus = { status: RestStatus.LOADED };
      })
      .addCase(fetchEvents.rejected, (state, action) => {
        state.eventsRestStatus = { status: RestStatus.LOAD_FAILED, error: action.error.message };
      })
      .addCase(fetchEvidence.pending, (state, action) => {
        if (!action.meta.arg.isFromCrossAppeal) {
          state.evidenceData = [];
          state.assessorEvidenceData = [];
          state.evidenceRestStatus = { status: RestStatus.LOADING };
        } else {
          state.crossEvidenceData = [];
          state.crossAssessorEvidenceData = [];
          state.crossEvidenceRestStatus = { status: RestStatus.LOADING };
        }
      })
      .addCase(fetchEvidence.fulfilled, (state, action) => {
        const evidence = action.payload.evidence;
        if (!action.meta.arg.isFromCrossAppeal) {
          state.evidenceData = evidence.filter(
            obj => obj.assessor !== true
          );
          state.assessorEvidenceData = evidence.filter(
            obj => obj.assessor === true
          );
          state.evidenceRestStatus = { status: RestStatus.LOADED }
        } else {
          state.crossEvidenceData = evidence.filter(
            obj => obj.assessor !== true
          );
          state.crossAssessorEvidenceData = evidence.filter(
            obj => obj.assessor === true
          );
          state.crossEvidenceRestStatus = { status: RestStatus.LOADED };
        }
      })
      .addCase(fetchEvidence.rejected, (state, action) => {
        if (!action.meta.arg.isFromCrossAppeal) {
          state.evidenceRestStatus = { status: RestStatus.LOAD_FAILED, error: action.error.message };
        } else {
          state.crossEvidenceRestStatus = { status: RestStatus.LOAD_FAILED, error: action.error.message };
        }
      })
      .addCase(addEvidenceDigest.pending, (state) => {
        state.evidenceRestStatus = { status: RestStatus.SAVING };
      })
      .addCase(addEvidenceDigest.fulfilled, (state) => {
        state.evidenceRestStatus = { status: RestStatus.SAVED };
      })
      .addCase(addEvidenceDigest.rejected, (state, action) => {
        state.evidenceRestStatus = { status: RestStatus.SAVE_FAILED, error: action.error.message };
      })
      .addCase(updateAppealStatus.pending, (state, action) => {
        state.statusRestStatus = { status: RestStatus.SAVING, requestId: action.meta.requestId };
      })
      .addCase(updateAppealStatus.fulfilled, (state, action) => {
        state.statusRestStatus = { status: RestStatus.SAVED };
        //console.log(action);
        state.appealStatus = action.payload.APPEAL.status;
      })
      .addCase(updateAppealStatus.rejected, (state, action) => {
        state.statusRestStatus = { status: RestStatus.SAVE_FAILED, error: action.error.message };
      })
      .addCase(updateContacts.pending, (state, action) => {
        state.contactsRestStatus = { status: RestStatus.SAVING, requestId: action.meta.requestId };
      })
      .addCase(updateContacts.fulfilled, (state, action) => {
        state.contactsRestStatus = { status: RestStatus.SAVED };
        populateContactsState(state, action.payload);
      })
      .addCase(updateContacts.rejected, (state, action) => {
        state.contactsRestStatus = { status: RestStatus.SAVE_FAILED, error: action.error.message };
      })
      .addCase(updateNotes.pending, (state) => {
        state.notesRestStatus = { status: RestStatus.SAVING };
      })
      .addCase(updateNotes.fulfilled, (state, action) => {
        state.notesRestStatus = { status: RestStatus.SAVED };
        state.notes = action.payload.APPEAL.notes;
      })
      .addCase(updateNotes.rejected, (state, action) => {
        state.notesRestStatus = { status: RestStatus.SAVE_FAILED, error: action.error.message };
      })
      .addCase(renumberAppeal.pending, (state) => {
        state.appealRestStatus = { status: RestStatus.SAVING };
      })
      .addCase(renumberAppeal.fulfilled, (state, action) => {
        const appeal = action.payload;
        if (!appeal) {
          state.appealRestStatus.status = RestStatus.LOAD_FAILED;
          state.appealRestStatus.error = 'Appeal was not found';
        } else {
          populateAppealState(state, appeal);
        }
        state.appealRestStatus = { status: RestStatus.SAVED };
      })
      .addCase(renumberAppeal.rejected, (state, action) => {
        state.appealRestStatus = { status: RestStatus.SAVE_FAILED, error: action.error.message };
      })
      .addCase(updateValuation.pending, (state) => {
        state.valuationRestStatus = { status: RestStatus.SAVING };
      })
      .addCase(updateValuation.fulfilled, (state, action) => {
        state.valuationRestStatus = { status: RestStatus.SAVED };
        state.valuation = action.payload.APPEAL.valuation;
        state.propertyValuationData[1].total = action.payload.APPEAL.valuation.requestedTotal
          ? action.payload.APPEAL.valuation.requestedTotal
          : ""
        state.data!.APPEAL.entryPaymentMethod = action.payload.APPEAL.entryPaymentMethod;
        state.data!.APPEAL.hearingType = action.payload.APPEAL.hearingType;
        state.data!.APPEAL.receiptId = action.payload.APPEAL.receiptId
      })
      .addCase(updateValuation.rejected, (state, action) => {
        state.valuationRestStatus = { status: RestStatus.SAVE_FAILED, error: action.error.message };
      })
      .addCase(updateImprovementAddition.pending, (state, action) => {
        state.appealRestStatus = { status: RestStatus.SAVING, requestId: action.meta.requestId };
      })
      .addCase(updateImprovementAddition.fulfilled, (state, action) => {
        state.improvementRecord = action.payload.APPEAL.improvementRecord;
        state.appealRestStatus = { status: RestStatus.SAVED };
      })
      .addCase(updateImprovementAddition.rejected, (state, action) => {
        state.appealRestStatus.status = RestStatus.SAVE_FAILED;
        state.appealRestStatus.error = action.error.message;
      })
      .addCase(withdrawAppeal.pending, (state, action) => {
        state.appealRestStatus = { status: RestStatus.SAVING, requestId: action.meta.requestId };
      })
      .addCase(withdrawAppeal.fulfilled, (state, action) => {
        populateAppealState(state, action.payload);
        state.appealRestStatus.status = RestStatus.SAVED;
      })
      .addCase(withdrawAppeal.rejected, (state, action) => {
        state.appealRestStatus.status = RestStatus.SAVE_FAILED;
        state.appealRestStatus.error = action.error.message;
      })
      .addCase(approveAppeal.pending, (state, action) => {
        state.appealRestStatus = { status: RestStatus.SAVING, requestId: action.meta.requestId };
      })
      .addCase(approveAppeal.fulfilled, (state, action) => {
        populateAppealState(state, action.payload);
        state.appealRestStatus.status = RestStatus.SAVED;
      })
      .addCase(approveAppeal.rejected, (state, action) => {
        state.appealRestStatus.status = RestStatus.SAVE_FAILED;
        state.appealRestStatus.error = action.error.message;
      })
      .addCase(updateScanFile.pending, (state, action) => {
        state.appealRestStatus = { status: RestStatus.SAVING, requestId: action.meta.requestId };
      })
      .addCase(updateScanFile.fulfilled, (state, action) => {
        populateAppealState(state, action.payload);
        state.appealRestStatus.status = RestStatus.SAVED;
      })
      .addCase(updateScanFile.rejected, (state, action) => {
        state.appealRestStatus.status = RestStatus.SAVE_FAILED;
        state.appealRestStatus.error = action.error.message;
      })
  },
});

export const { setCounty } = appealDetailSlice.actions;
export const selectAppealNumber = (state: RootState) => state.appealDetail.data?.APPEAL.appealNumber;
export const selectAppealStatus = (state: RootState) => state.appealDetail.appealStatus;
export const selectCounty = (state: RootState) => state.appealDetail.county;
export const selectIsAssessorCounty = (state: RootState) => state.appealDetail.county?.key !== '11';
export const selectDatabaseId = (state: RootState) => state.appealDetail.data?.APPEAL.database_id;
export const selectEvidence = (state: RootState) => state.appealDetail.evidenceData;
export const selectAssessorEvidence = (state: RootState) => state.appealDetail.assessorEvidenceData;
export const selectCrossEvidence = (state: RootState) => state.appealDetail.crossEvidenceData;
export const selectCrossAssessorEvidence = (state: RootState) => state.appealDetail.crossAssessorEvidenceData;
export const selectInLockoutPeriod = (state: RootState) => state.appealDetail.inLockOutPeriod;
export const selectDeadlineForEvidence = (state: RootState) => state.appealDetail.deadlineForEvidence;
export const selectInEndState = (state: RootState) => state.appealDetail.inEndState;
export const selectNotes = (state: RootState) => state.appealDetail.data?.APPEAL.notes;
export const selectNotesRestStatus = (state: RootState) => state.appealDetail.notesRestStatus;
export const selectAbatementValue = (state: RootState) => state.appealDetail.data?.APPEAL.property.currentAssessment.abatement;
export const selectAppellantPhone = (state: RootState) => state.appealDetail.contact?.dayTimePhone;
export const selectAdditionalContactCellPhoneNumber = (state: RootState) => state.appealDetail.additionalContact?.dayTimePhone;
export const selectThirdPartyContactCellPhoneNumber = (state: RootState) => state.appealDetail.thirdPartyContact?.dayTimePhone;
export const selectAuthorizedAttorneyContactCellPhoneNumber = (state: RootState) => state.appealDetail.authorizedAttorneyContact?.dayTimePhone;
export const selectAppellantEmail = (state: RootState) => state.appealDetail.contact?.emailAddress;
export const selectAdditionalContactEmail = (state: RootState) => state.appealDetail.additionalContact?.emailAddress;
export const selectThirdPartyContactEmail = (state: RootState) => state.appealDetail.thirdPartyContact?.emailAddress;
export const selectAuthorizedAttorneyContactEmail = (state: RootState) => state.appealDetail.authorizedAttorneyContact?.emailAddress;
export const selectContact = (state: RootState) => state.appealDetail.contact;
export const selectAdditionalContact = (state: RootState) => state.appealDetail.additionalContact;
export const selectThirdPartyContact = (state: RootState) => state.appealDetail.thirdPartyContact;
export const selectMunicipalContact = (state: RootState) => state.appealDetail.thirdPartyContact;
export const selectAuthorizedAttorneyContact = (state: RootState) => state.appealDetail.authorizedAttorneyContact;
export const selectAdditionalContactByEmail = (state: RootState) => state.appealDetail.additionalContact?.contactEmailCheckbox;
export const selectAdditionalContactByPhone = (state: RootState) => state.appealDetail.additionalContact?.contactPhoneCheckbox;
export const selectContactByEmail = (state: RootState) => state.appealDetail.contact?.contactEmailCheckbox;
export const selectContactByPhone = (state: RootState) => state.appealDetail.contact?.contactPhoneCheckbox;
export const selectAuthorizedAttorneyContactByEmail = (state: RootState) => state.appealDetail.authorizedAttorneyContact?.authorizedAttorneyEmailCheckbox;
export const selectAuthorizedAttorneyContactByPhone = (state: RootState) => state.appealDetail.authorizedAttorneyContact?.authorizedAttorneyPhoneCheckbox;
export const selectThirdPartyContactByEmail = (state: RootState) => state.appealDetail.thirdPartyContact?.thirdPartyEmailCheckbox;
export const selectThirdPartyContactByPhone = (state: RootState) => state.appealDetail.thirdPartyContact?.thirdPartyPhoneCheckbox;
export const selectHearingType = (state: RootState) => state.appealDetail.data?.APPEAL.hearingType;
export const selectIsAppealingValuation = (state: RootState) => state.appealDetail.valuation?.appealing;
export const selectPurchaseDate = (state: RootState) => state.appealDetail.data?.APPEAL.property.purchaseDate;
export const selectPurchasePrice = (state: RootState) => state.appealDetail.data?.APPEAL.property.purchasePrice;
export const selectValuationPurchaseDate = (state: RootState) => state.appealDetail.valuation?.purchaseDate;
export const selectValuationPurchasePrice = (state: RootState) => state.appealDetail.valuation?.purchasePrice;
export const selectValuationRestStatus = (state: RootState) => state.appealDetail.valuationRestStatus;
export const selectReasonForAppeal = (state: RootState) => state.appealDetail.valuation?.reason;
export const selectAWOP = (state: RootState) => state.appealDetail.valuation?.awop;
export const selectTaxCourtPending = (state: RootState) => state.appealDetail.valuation?.taxCourtPending;
export const selectPropertyValuationData = (state: RootState) => state.appealDetail.propertyValuationData;
export const selectAppealEnteredByUUID = (state: RootState) => state.appealDetail.data?._UUID;
export const selectJudgmentResult = (state: RootState) => state.appealDetail.judgment?.judgementResult;
export const selectJudgmentResultTwo = (state: RootState) => state.appealDetail.judgment?.judgementResultTwo;
export const selectJudgmentAmount = (state: RootState) => state.appealDetail.judgment?.judgementAmount;
export const selectJudgmentPropertyClass = (state: RootState) => state.appealDetail.judgment?.judgementPropertyClass;
export const selectJudgmentExplanation = (state: RootState) => state.appealDetail.judgment?.judgementExplanation;
export const selectJudgmentPresentSelected = (state: RootState) => state.appealDetail.judgment?.presentSelected;
export const selectJudgmentAddedDate = (state: RootState) => state.appealDetail.judgment?.judgementAddedDate;
export const selectJudgmentMailDate = (state: RootState) => state.appealDetail.judgment?.judgementMailDate;
export const selectImprovementRecord = (state: RootState) => state.appealDetail.improvementRecord;
export const selectRequestedAdditionLand = (state: RootState) => state.appealDetail.improvementRecord?.requestedAdditionLand;
export const selectTaxCourtTaxYear = (state: RootState) => state.appealDetail.judgment?.taxCourtTaxYear;
export const selectRevisedAcreage = (state: RootState) => state.appealDetail.judgment?.revisedAcreage;
export const selectRequestedAdditionImprovement = (state: RootState) => state.appealDetail.improvementRecord?.requestedAdditionImprovement;
export const selectRequestedAdditionAbatement = (state: RootState) => state.appealDetail.improvementRecord?.requestedAdditionAbatement;
export const selectRequestedAdditionAmount = (state: RootState) => state.appealDetail.improvementRecord?.requestedAdditionAmount;
export const selectRequestedAdditionDate = (state: RootState) => state.appealDetail.improvementRecord?.requestedAdditionDate;
export const selectRequestedAdditionMonth = (state: RootState) => state.appealDetail.improvementRecord?.requestedAdditionMonth;
export const selectAdditionPropertyClass = (state: RootState) => state.appealDetail.improvementRecord?.propertyClass;
export const selectAppealingForClassification = (state: RootState) => state.appealDetail.data?.APPEAL.classification.appealing
export const selectAbatementDeductionAppeal = (state: RootState) => state.appealDetail.data?.APPEAL.deduction.abatement;
export const selectVeteransPropertyDeductionAppeal = (state: RootState) => state.appealDetail.data?.APPEAL.deduction.veteranDeduction;
export const selectDisabledVeteranDeductionAppeal = (state: RootState) => state.appealDetail.data?.APPEAL.deduction.disabledVeteranExemption;
export const selectSeniorDisabledDeductionAppeal = (state: RootState) => state.appealDetail.data?.APPEAL.deduction.seniorCitizenOrDisabledPerson;
export const selectClassification = (state: RootState) => state.appealDetail.data?.APPEAL.classification;
export const selectEntryMethod = (state: RootState) => state.appealDetail.data?.APPEAL.entryMethod;
export const selectHearingDate = (state: RootState) => state.appealDetail.data?.APPEAL.hearingDate;
export const selectHearingDateFinalized = (state: RootState) => state.appealDetail.data?.APPEAL.hearingDateFinalized ? true : false;
export const selectImprovementType = (state: RootState) => state.appealDetail.data?.APPEAL.improvementRecord?.originalRecord?.type;
export const selectImprovementValue = (state: RootState) => state.appealDetail.data?.APPEAL.property.currentAssessment.buildingImprovement;
export const selectLandValue = (state: RootState) => state.appealDetail.data?.APPEAL.property.currentAssessment.land;
export const selectLotSize = (state: RootState) => state.appealDetail.data?.APPEAL.property.lotSize;
export const selectMunicipality = (state: RootState) => state.appealDetail.data?.APPEAL.municipality.label;
export const selectMunicipalityKey = (state: RootState) => state.appealDetail.data?.APPEAL.municipality.key;
export const selectPropertyAddress = (state: RootState) => state.appealDetail.data?.APPEAL.property.address;
export const selectPropertyBlq = (state: RootState) => state.appealDetail.data?.APPEAL.property.blq;
export const selectPropertyLocation = (state: RootState) => state.appealDetail.data?.APPEAL.property.relevantProperty;
export const selectPropertyZip = (state: RootState) => state.appealDetail.data?.APPEAL.property.zip;
export const selectReceivedReasonForWithdrawal = (state: RootState) => state.appealDetail.data?.APPEAL.reasonForWithdrawal;
export const selectReceiptId = (state: RootState) => state.appealDetail.data?.APPEAL.receiptId;
export const selectScanFile = (state: RootState) => state.appealDetail.data?.APPEAL.scanFile;
export const selectTenAppeal = (state: RootState) => state.appealDetail.data?.APPEAL.tenAppeal;
export const selectTotalValue = (state: RootState) => state.appealDetail.data?.APPEAL.property.currentAssessment.total;
export const selectStatusRestStatus = (state: RootState) => state.appealDetail.statusRestStatus;
export const selectStipExists = (state: RootState) => state.appealDetail.stipulation !== undefined && state.appealDetail.stipulation !== null;
export const selectStipFileName = (state: RootState) => state.appealDetail.stipulation?.fileName;
export const selectStipFileUrl = (state: RootState) => state.appealDetail.stipulation?.stipFile;
export const selectStipStatus = (state: RootState) => state.appealDetail.stipulation?.status;
export const selectStipTotal = (state: RootState) => state.appealDetail.stipulation?.stipAmount.total;
export const selectReceivedStipBasisForSettlement = (state: RootState) => state.appealDetail.stipulation?.basisForSettlement;
export const selectStipLandAmount = (state: RootState) => state.appealDetail.stipulation ? state.appealDetail.stipulation?.stipAmount.landAmount : state.appealDetail.data?.APPEAL.property.currentAssessment.land;
export const selectStipBuildingAmount = (state: RootState) => state.appealDetail.stipulation ? state.appealDetail.stipulation?.stipAmount.buildingAmount :
  state.appealDetail.data?.APPEAL.deduction.veteranDeduction ||
    state.appealDetail.data?.APPEAL.deduction.disabledVeteranExemption || state.appealDetail.data?.APPEAL.deduction.seniorCitizenOrDisabledPerson
    ? state.appealDetail.data?.APPEAL.property.currentAssessment.buildingImprovement : 0;
export const selectStipAbatementAmount = (state: RootState) => state.appealDetail.stipulation ? state.appealDetail.stipulation?.stipAmount.abatementAmount : state.appealDetail.data?.APPEAL.property.currentAssessment.abatement;
export const selectStipMonth = (state: RootState) => state.appealDetail.stipulation ? state.appealDetail.stipulation?.requestedAdditionMonth : state.appealDetail.improvementRecord?.requestedAdditionMonth;
export const selectStipRequestedAdditionMonth = (state: RootState) => state.appealDetail.stipulation ? state.appealDetail.stipulation?.requestedAdditionMonth : state.appealDetail.data?.APPEAL.improvementRecord.requestedAdditionMonth;
export const selectStipRequestedAdditionAmount = (state: RootState) => state.appealDetail.stipulation ? state.appealDetail.stipulation?.stipAmount?.total : state.appealDetail.data?.APPEAL.improvementRecord.requestedAdditionAmount;
export const selectStipRequestedAdditionLand = (state: RootState) => state.appealDetail.stipulation ? state.appealDetail.stipulation?.stipAmount?.landAmount : state.appealDetail.data?.APPEAL.improvementRecord.requestedAdditionLand;
export const selectStipRequestedAdditionImprovement = (state: RootState) => state.appealDetail.stipulation ? state.appealDetail.stipulation?.stipAmount?.buildingAmount : state.appealDetail.data?.APPEAL.improvementRecord.requestedAdditionImprovement;
export const selectStipRequestedAdditionAbatement = (state: RootState) => state.appealDetail.stipulation ? state.appealDetail.stipulation?.stipAmount?.abatementAmount : state.appealDetail.data?.APPEAL.improvementRecord.requestedAdditionAbatement;
export const selectStipPropertyClass = (state: RootState) => state.appealDetail.stipulation?.propertyClass;
export const selectCreatorObj = (state: RootState) => state.appealDetail.data?.APPEAL.creatorObj;
export const selectEventsData = (state: RootState) => state.appealDetail.eventsData;
export const selectFetchAppealError = (state: RootState) => state.appealDetail.appealRestStatus?.error;
export const selectAppealRestStatus = (state: RootState) => state.appealDetail.appealRestStatus;
export const selectEventsRestStatus = (state: RootState) => state.appealDetail.eventsRestStatus;
export const selectJudgmentRestStatus = (state: RootState) => state.appealDetail.judgmentRestStatus;
export const selectEntryPaymentMethod = (state: RootState) => state.appealDetail.data?.APPEAL.entryPaymentMethod;
export const selectSubmitDate = (state: RootState) => state.appealDetail.data?.APPEAL.submitDate;
export const selectPropertyClass = (state: RootState) => state.appealDetail.data?.APPEAL.property.classification;
export const selectPropertyClassKey = (state: RootState) => state.appealDetail.data?.APPEAL.property.classification?.key;
export const selectOwnerName = (state: RootState) => state.appealDetail.data?.APPEAL.contact.firstName;
export const selectOwnerAddress = (state: RootState) => state.appealDetail.data?.APPEAL.contact.address;
export const selectOwnerCityState = (state: RootState) => state.appealDetail.data?.APPEAL.contact.city;
export const selectOwnerZip = (state: RootState) => state.appealDetail.data?.APPEAL.contact.zipCode;
export const selectContactName = (state: RootState) => state.appealDetail.data?.APPEAL.contact.firstName;
export const selectContactAddress = (state: RootState) => state.appealDetail.data?.APPEAL.contact.address;
export const selectContactCityState = (state: RootState) => state.appealDetail.data?.APPEAL.contact.city;
export const selectContactZip = (state: RootState) => state.appealDetail.data?.APPEAL.contact.zipCode;
export const selectRequestedTotalValue = (state: RootState) => state.appealDetail.data?.APPEAL.valuation.requestedTotal;
export const selectReceivedClassification = (state: RootState) => state.appealDetail.data?.APPEAL.classification.requestedClass?.label;
export const selectComparablesData = (state: RootState) => state.appealDetail.comparablesData;
export const selectComparablesRestStatus = (state: RootState) => state.appealDetail.comparablesRestStatus;
export const selectAssessorComparablesData = (state: RootState) => state.appealDetail.assessorComparablesData;
export const selectCrossComparablesData = (state: RootState) => state.appealDetail.crossComparablesData;
export const selectCrossAssessorComparablesData = (state: RootState) => state.appealDetail.crossAssessorComparablesData;
export const selectRequestedClass = (state: RootState) => state.appealDetail.data?.APPEAL.classification.requestedClass?.key;
export const selectSolicitorApproved = (state: RootState) => state.appealDetail.data?.APPEAL.creatorObj.approved;
export const selectApproverObject = (state: RootState) => state.appealDetail.data?.APPEAL.creatorObj.approverObject;
export const selectContactLawyerLicenseNumber = (state: RootState) => state.appealDetail.additionalContact?.aCLawyerLicense;
export const selectAppeal = (state: RootState) => state.appealDetail.data;
export const selectAssessorObj = (state: RootState) => state.appealDetail.stipulation?.assessorObj;
export const selectWithdrawnDate = (state: RootState) => state.appealDetail.withdrawnDate;
export const selectPetitioner = (state: RootState) => state.appealDetail.petitioner;
export const selectRespondent = (state: RootState) => state.appealDetail.respondent;
export const selectIsAssessorAppeal = (state: RootState) => state.appealDetail.isAssessorAppeal;
export const selectIsCrossAppeal = (state: RootState) => state.appealDetail.data?.APPEAL.crossAppeal.isCrossAppeal;
export const selectIsCrossedAppeal = (state: RootState) => state.appealDetail.data?.APPEAL.crossAppeal.isCrossedAppeal;
export const selectOtherAppealNumber = (state: RootState) => state.appealDetail.data?.APPEAL.crossAppeal.otherAppeal;
export const selectFees = (state: RootState) => state.appealDetail.data?.APPEAL.fees;
export const selectCreditCardPayment = (state: RootState) => state.appealDetail.data?.APPEAL.creditCardPayment;
export const selectContactsRestStatus = (state: RootState) => state.appealDetail.contactsRestStatus;
export const selectSaveContactsError = (state: RootState) => state.appealDetail.contactsRestStatus?.error;
export const selectAppealYear = (state: RootState) => state.appealDetail.data?.APPEAL?.appealYear;
export default appealDetailSlice.reducer;